/* eslint-disable react/prop-types */
import { FC, useEffect } from "react";
import {
	FormProvider,
	SubmitHandler,
	useForm,
	useFormState
} from "react-hook-form";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "src/components/ui/button";
import {
	FormField,
	FormItem,
	FormLabel,
	FormControl
} from "src/components/ui/form";
import { Input } from "src/components/ui/input";
import { Checkbox } from "src/components/ui/checkbox";
import isEmpty from "lodash.isempty";

export interface QuickLinkFormValues {
	heading: string;
	content: string;
	buttonLink: string;
	webview: boolean;
}

interface Props {
	defaultValues: QuickLinkFormValues;
	onSubmit: SubmitHandler<QuickLinkFormValues>;
	isSubmitting?: boolean;
	isEdit?: boolean;
	onDelete?: () => void;
}

const QuickLinkForm: FC<Props> = ({
	defaultValues,
	onSubmit,
	isSubmitting = false,
	isEdit = false,
	onDelete
}) => {
	const methods = useForm<QuickLinkFormValues>({
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues
	});

	const { reset, control, handleSubmit, watch } = methods;
	const { isDirty, errors } = useFormState({ control });

	const isSubmitBtnDisabled = !isDirty || !isEmpty(errors) || isSubmitting;

	useEffect(() => {
		reset(defaultValues);
	}, [defaultValues]);

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={(e) => {
				if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
					e.preventDefault();
				}
			}}
		>
			<FormProvider {...methods}>
				<div className="flex flex-col gap-4">
					{/* Heading */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							errors.heading ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{errors.heading && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									Please fill out the required field
								</span>
							</div>
						)}
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Heading text
							</label>
							<FormField
								control={control}
								name="heading"
								rules={{
									required: "Heading is required",
									maxLength: {
										value: 40,
										message: "Maximum 40 characters"
									}
								}}
								render={({ field }) => (
									<FormItem className="space-y-0">
										<FormControl>
											<Input
												placeholder="Maximum heading count is 40 characters."
												maxLength={40}
												className="w-full bg-white"
												{...field}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<p className="text-[13px] text-zinc-500 text-right mt-1">
								{watch("heading")?.length || 0} / 40
							</p>
						</div>
					</div>

					{/* Content */}
					<div className="rounded-xl border border-border bg-subtle shadow-sm">
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Content text
							</label>
							<FormField
								control={control}
								name="content"
								rules={{
									maxLength: {
										value: 40,
										message: "Maximum 40 characters"
									}
								}}
								render={({ field }) => (
									<FormItem className="space-y-0">
										<FormControl>
											<Input
												placeholder="Maximum content count is 40 characters."
												maxLength={40}
												className="w-full bg-white"
												{...field}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<p className="text-[13px] text-zinc-500 text-right mt-1">
								{watch("content")?.length || 0} / 40
							</p>
						</div>
					</div>

					{/* Link (optional) */}
					<div className="rounded-xl border border-border bg-subtle shadow-sm">
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Link (optional)
							</label>

							<div className="flex flex-col gap-5">
								<FormField
									control={control}
									name="buttonLink"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm font-medium text-zinc-600">
												URL
											</FormLabel>
											<FormControl>
												<Input
													placeholder="https://example.com"
													className="w-full bg-white"
													{...field}
												/>
											</FormControl>
										</FormItem>
									)}
								/>

								<FormField
									control={control}
									name="webview"
									render={({ field }) => (
										<FormItem className="flex items-center gap-2">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
											<FormLabel className="!mt-0 text-sm text-zinc-700 cursor-pointer">
												Open in app
											</FormLabel>
										</FormItem>
									)}
								/>
							</div>
						</div>
					</div>
				</div>

				<div className="flex items-center justify-between pt-8 mt-8 border-t border-border">
					<div className="flex items-center gap-4">
						<Link
							to="/app/homepage/quick-links"
							className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-foreground transition-colors"
						>
							<ArrowLeft className="h-4 w-4" />
							Back to quick links
						</Link>

						{isEdit && onDelete && (
							<Button type="button" variant="destructive" onClick={onDelete}>
								Delete
							</Button>
						)}
					</div>

					<Button type="submit" disabled={isSubmitBtnDisabled}>
						{isSubmitting ? (
							<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
						) : (
							"Save"
						)}
					</Button>
				</div>
			</FormProvider>
		</form>
	);
};

export default QuickLinkForm;
